// Env shape passed from Astro pages (`Astro.locals.runtime.env` on Cloudflare,
// or any Record<string, string | undefined>). Keeps the data layer
// runtime-agnostic — the caller sources env values.
export type Env = Record<string, string | undefined>;

/**
 * One entry per CMS collection wired into this app. Adding a fifth resource
 * type is a four-line change: add it here, to RESOURCE_TYPES, and to
 * TYPE_LABEL / TYPE_PRIORITY / COLLECTION_SLUG / ENV_VAR_BY_TYPE in
 * `collections.ts`.
 */
export type ResourceType = "ebook" | "report" | "webinar" | "whitepaper";

export const RESOURCE_TYPES: readonly ResourceType[] = [
  "ebook",
  "report",
  "webinar",
  "whitepaper",
] as const;

/** A filter chip value — the four types plus the "everything" pseudo-value. */
export type FilterValue = ResourceType | "all";

/**
 * Grid ordering. Affects only cards without a curated position — see the note
 * in `sort.ts` for why curated cards are never reversed.
 */
export type SortValue = "latest" | "oldest";

/** The slim, serializable shape handed from SSR into the React island. */
export type ResourceCard = {
  id: string;
  type: ResourceType;
  typeLabel: string;
  title: string;
  description: string;
  image: { url: string; alt: string | null } | null;
  slug: string;
  /** Absolute path to the Webflow-hosted detail page. */
  url: string;
  lastPublished: string;
  /**
   * The editorial publication date from the CMS (`Original Publish(ed) Date`),
   * ISO 8601, and the **primary sort key** for uncurated cards. `null` when the
   * collection has no such field, when it is empty, or when its value will not
   * parse — in which case `sort.ts` falls back to `lastPublished`.
   *
   * `lastPublished` is a *site-publish* timestamp: republishing the site rewrites
   * it for every item at once, which collapses the grid's ordering into "whatever
   * order Webflow happened to write them in". That is why this field exists.
   */
  originalPublishDate: string | null;
  /**
   * Curated position, ascending. Always `null` today — the ordering source
   * (CSV snapshot or a D1 `resource_order` table) is the documented extension
   * point; see `sort.ts`. Cards with `null` sort by `originalPublishDate` desc.
   */
  manualOrder: number | null;
};
